import type {
  TExcursionFiltersState,
  TExcursionFiltersTourFormat,
  TExcursionFiltersType,
} from '@/entities/filter/store/types/excursion/state';
import type { LocationQuery } from 'vue-router';
import { DEFAULT_SORTING } from '@/entities/filter/store/const/config';
import { START_TIME_VALUE, type StartTimeValue } from '@/shared/types/basic';

export function getExcursionInitialFiltersState(): TExcursionFiltersState {
  return {
    date: {
      from: null,
      to: null,
      persons: 1,
    },
    type: [],
    tourFormat: [],
    movementTypes: null,
    price: [null, null],
    selected_rubric: null,
    tag__slugs: 'all',
    sorting: 'rank',
    duration: [null, null],
    startTimeInterval: [],
    childrenAllowed: null,
    durationInterval: [],
  };
}

function normalizeTagSlug(tag: string) {
  return tag.replace(/^\d+-/, '');
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function setExcursionInitialFilterState(queryParams: LocationQuery, tag: string) {
  const defaultParams = {
    date: {
      from: null,
      to: null,
      persons: 1,
    },
    type: [],
    tourFormat: [],
    movementTypes: null,
    price: [null, null],
    selected_rubric: null,
    tag__slugs: 'all',
    sorting: null,
    duration: [null, null],
    startTimeInterval: [],
    childrenAllowed: null,
    durationInterval: [],
  } as TExcursionFiltersState;

  if (queryParams.start_price || queryParams.end_price) {
    defaultParams.price = [
      queryParams.start_price ? Number(queryParams.start_price) : null,
      queryParams.end_price ? Number(queryParams.end_price) : null,
    ];
  }

  if (queryParams.start_date || queryParams.end_date || queryParams.persons_count) {
    defaultParams.date = {
      from: queryParams.start_date ? queryParams.start_date.toString() : null,
      to: queryParams.end_date ? queryParams.end_date.toString() : null,
      persons: queryParams.persons_count ? Number(queryParams.persons_count) : 1,
    };
  }

  if (queryParams.type) {
    const rawTypes = queryParams.type.toString().split(',');
    // Для разделения фильтров формата проведения туров было
    // внедрено дублирование функционала. В сторе заведено еще одно состояние tourFormat
    // которое дублирует состояние type у фильтров формата экскурсий
    // в апи и в урлах значение из tourFormat приравнивается к апи полю type

    const tourTypes = rawTypes.filter((v) => v.startsWith('tour_')) as TExcursionFiltersTourFormat;
    const baseTypes = rawTypes.filter((v) => !v.startsWith('tour_'));

    // Всегда сохраняем tour_* в отдельное состояние
    defaultParams.tourFormat = tourTypes;

    // Если базовых типов нет — это "чистые туры", формат экскурсий тут не нужен
    if (!baseTypes.length) {
      defaultParams.type = [];
    } else {
      const pairs = [
        { base: 'private', tour: 'tour_private', value: 'private,tour_private' },
        { base: 'group', tour: 'tour_group', value: 'group,tour_group' },
        { base: 'minigroup', tour: 'tour_minigroup', value: 'minigroup,tour_minigroup' },
      ];

      const result: string[] = [];

      for (const p of pairs) {
        // смотрим в rawTypes, где есть и base, и tour_ вариант
        if (rawTypes.includes(p.base) && rawTypes.includes(p.tour)) {
          result.push(p.value);
        }
      }

      // fallback — если по какой-то причине пары не собрались, сохраняем как есть
      if (!result.length) {
        result.push(baseTypes.join(','));
      }

      defaultParams.type = result as TExcursionFiltersType;
    }
  }

  if (queryParams.movement_type) {
    defaultParams.movementTypes = queryParams.movement_type ? queryParams.movement_type.toString().split(',') : null;
  }

  if (queryParams.start_duration || queryParams.end_duration) {
    defaultParams.duration = [
      queryParams.start_duration ? Number(queryParams.start_duration) : null,
      queryParams.end_duration ? Number(queryParams.end_duration) : null,
    ];
  }

  if (queryParams.sorting) {
    defaultParams.sorting = queryParams.sorting as string;
  } else {
    defaultParams.sorting = DEFAULT_SORTING;
  }
  if (queryParams.start_time_interval) {
    defaultParams.startTimeInterval = queryParams.start_time_interval
      .toString()
      .split(',')
      .filter((time): time is StartTimeValue => Object.values(START_TIME_VALUE).includes(time as StartTimeValue));
  }

  if (queryParams.children_allowed) {
    defaultParams.childrenAllowed = true;
  }

  if (queryParams.duration_interval) {
    defaultParams.durationInterval = queryParams.duration_interval.toString().split(',');
  }

  if (tag) {
    defaultParams.tag__slugs = normalizeTagSlug(tag);
  }

  return defaultParams;
}
